import { Link } from "react-router";

import { useInfiniteQuery } from "@tanstack/react-query";

import { ArrowRightIcon, BellIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";

import { EmptyState } from "@/shared/components/EmptyState";
import { QUERY_EXPIRATIONS, QUERY_KEYS } from "@/shared/lib/constants/queries";
import { getMyNotifications } from "@/features/notifications/services/notifications";
import { ApiNotificationType } from "@/features/notifications/types";

import { TYPE_CONFIG } from "../config";
import { isUnread } from "../helpers";
import { NotificationCard } from "./NotificationCard";
import { NotificationSkeleton } from "./NotificationSkeleton";

export function NotificationBell({ permissions }: { permissions: string[] }) {
  const { data, isPending } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.GET_MY_NOTIFICATIONS],
    queryFn: ({ pageParam }) => getMyNotifications(pageParam),
    initialPageParam: null as number | null,
    getNextPageParam: (lastPage) => lastPage.nextStartingAfter,
    staleTime: QUERY_EXPIRATIONS.GET_MY_NOTIFICATIONS,
  });

  const notifications = data?.pages.flatMap((p) => p.notifications) ?? [];
  const unreadCount = notifications.filter(isUnread).length;
  const preview = notifications.slice(0, 5);

  const typeGroups = (["ProjectInvitation", "ProjectRemoval", "ProjectRoleUpdate"] as ApiNotificationType[]).map((type) => ({ type, count: notifications.filter((n) => n.type === type).length })).filter((g) => g.count > 0);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative hidden rounded-full md:flex" aria-label="Notifications">
          <BellIcon className="size-4" />
          {!isPending && unreadCount > 0 && (
            <span className="absolute right-1.5 top-1.5 flex size-2 items-center justify-center bg-primary">
              <span className="absolute inline-flex h-full w-full animate-ping bg-primary opacity-75" />
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-[420px] p-0 shadow-lg" sideOffset={8}>
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
          <div className="flex items-center gap-2">
            <BellIcon className="size-4 text-primary" />
            <span className="text-sm font-semibold tracking-wider uppercase">Notifications</span>
            {!isPending && unreadCount > 0 && <Badge className="px-1.5 py-0 text-[10px]">{unreadCount}</Badge>}
          </div>
        </div>

        {/* Type summary chips */}
        {!isPending && typeGroups.length > 0 && (
          <div className="flex flex-wrap gap-1.5 border-b px-4 py-2.5">
            {typeGroups.map(({ type, count }) => {
              const cfg = TYPE_CONFIG[type];
              const Icon = cfg.icon;
              return (
                <div key={type} className={`flex items-center gap-1 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider ${cfg.bgClass}`}>
                  <Icon className={`size-3 ${cfg.iconClass}`} />
                  {count} {cfg.label}
                </div>
              );
            })}
          </div>
        )}

        {/* Notification list */}
        {isPending ? (
          <div className="flex flex-col divide-y">
            {Array.from({ length: 3 }).map((_, i) => (
              <NotificationSkeleton key={i} />
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <EmptyState icon={BellIcon} title="You're all caught up" description="No notifications right now." className="py-12" />
        ) : (
          <div className="max-h-[420px] overflow-y-auto">
            {preview.map((n, i) => (
              <div key={n.id}>
                <NotificationCard notification={n} permissions={permissions} />
                {i < preview.length - 1 && <Separator />}
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        {!isPending && notifications.length > 0 && (
          <div className="border-t px-4 py-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">{notifications.length}</span> notification{notifications.length !== 1 ? "s" : ""}
                {notifications.length > 5 && <span className="text-muted-foreground"> · showing {preview.length}</span>}
              </p>
              <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs" asChild>
                <Link to="/notifications">
                  View all
                  <ArrowRightIcon className="size-3" />
                </Link>
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
